import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { api } from '../utils/api';
import '../styles/Admin.css';

function Admin() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [ageLimitDays, setAgeLimitDays] = useState('');
  const [ageLimitLoading, setAgeLimitLoading] = useState(true);
  const [ageLimitError, setAgeLimitError] = useState('');
  const [ageLimitSaved, setAgeLimitSaved] = useState(false);
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    full_name: '',
    is_admin: false
  });
  const { token, isAdmin } = useAuth();

  useEffect(() => {
    if (isAdmin) {
      fetchUsers();
      fetchReportAgeLimit();
    }
  }, [isAdmin]);

  const fetchUsers = async () => {
    try {
      const data = await api.getUsers(token);
      setUsers(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchReportAgeLimit = async () => {
    try {
      const data = await api.getReportAgeLimit(token);
      setAgeLimitDays(data.days === null ? '' : String(data.days));
      setAgeLimitError('');
    } catch (err) {
      setAgeLimitError(err.message);
    } finally {
      setAgeLimitLoading(false);
    }
  };

  const handleSaveAgeLimit = async () => {
    try {
      setAgeLimitError('');
      setAgeLimitSaved(false);

      const days = ageLimitDays === '' ? null : parseInt(ageLimitDays);

      if (ageLimitDays !== '' && (isNaN(days) || days < 1)) {
        setAgeLimitError('Antall dager må være et positivt tall');
        return;
      }

      await api.updateReportAgeLimit(token, days);
      setAgeLimitSaved(true);

      // Clear success message after 3 seconds
      setTimeout(() => setAgeLimitSaved(false), 3000);
    } catch (err) {
      setAgeLimitError(err.message);
    }
  };

  const handleEdit = (user) => {
    setEditingUser(user);
    setFormData({
      username: user.username,
      password: '',
      full_name: user.full_name,
      is_admin: user.is_admin === 1
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    try {
      if (editingUser) {
        // Update existing user
        const updateData = {
          username: formData.username,
          full_name: formData.full_name,
          is_admin: formData.is_admin
        };
        // Only include password if it's been filled in
        if (formData.password) {
          updateData.password = formData.password;
        }
        await api.updateUser(token, editingUser.id, updateData);
      } else {
        // Create new user
        await api.createUser(token, formData);
      }
      setShowModal(false);
      setEditingUser(null);
      setFormData({
        username: '',
        password: '',
        full_name: '',
        is_admin: false
      });
      fetchUsers();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingUser(null);
    setFormData({
      username: '',
      password: '',
      full_name: '',
      is_admin: false
    });
    setError('');
  };

  const handleDisable = async (id) => {
    if (!window.confirm('Er du sikker på at du vil deaktivere denne brukeren?')) {
      return;
    }

    try {
      await api.disableUser(token, id);
      setUsers(users.map(u => u.id === id ? { ...u, is_active: 0 } : u));
    } catch (err) {
      alert('Feil ved deaktivering: ' + err.message);
    }
  };

  const handleEnable = async (id) => {
    try {
      await api.enableUser(token, id);
      setUsers(users.map(u => u.id === id ? { ...u, is_active: 1 } : u));
    } catch (err) {
      alert('Feil ved aktivering: ' + err.message);
    }
  };

  const handleEnableEmailDigest = async (id) => {
    try {
      await api.enableEmailDigest(token, id);
      setUsers(users.map(u => u.id === id ? { ...u, email_digest_allowed: 1 } : u));
    } catch (err) {
      alert('Feil ved aktivering av e-postvarsler: ' + err.message);
    }
  };

  const handleDisableEmailDigest = async (id) => {
    if (!window.confirm('Er du sikker på at du vil deaktivere e-postvarsler for denne brukeren?')) {
      return;
    }
    try {
      await api.disableEmailDigest(token, id);
      setUsers(users.map(u => u.id === id ? { ...u, email_digest_allowed: 0 } : u));
    } catch (err) {
      alert('Feil ved deaktivering av e-postvarsler: ' + err.message);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('no-NO');
  };

  if (!isAdmin) {
    return (
      <div className="access-denied">
        <h2>Ingen tilgang</h2>
        <p>Du har ikke tilgang til administrasjonssiden.</p>
      </div>
    );
  }

  if (loading) {
    return <div className="loading">Laster brukere...</div>;
  }

  return (
    <div className="admin-container">
      <div className="admin-header">
        <h1>Administrasjon</h1>
        <div className="admin-actions">
          <Link to="/admin/audit-log" className="btn-secondary">
            Revisjonslogg
          </Link>
          <Link to="/admin/email-settings" className="btn-secondary">
            E-postinnstillinger
          </Link>
          <button onClick={() => setShowModal(true)} className="btn-primary">
            Ny bruker
          </button>
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}

      {/* Report Access Control Section */}
      {!ageLimitLoading && (
        <div className="settings-section">
          <h2 className="section-heading">Rapporttilgangskontroll</h2>
          <div className="settings-content">
            <div className="setting-group">
              <label htmlFor="ageLimit">Maksimal rapportalder (dager):</label>
              <div className="input-group">
                <input
                  type="number"
                  id="ageLimit"
                  min="1"
                  value={ageLimitDays}
                  onChange={(e) => setAgeLimitDays(e.target.value)}
                  placeholder="La stå tomt for ubegrenset"
                  className="age-limit-input"
                />
                <button
                  onClick={handleSaveAgeLimit}
                  className="btn-primary"
                >
                  Lagre
                </button>
              </div>
              {ageLimitDays === '' && <small className="info-text">Ubegrenset - brukere har tilgang til alle rapporter</small>}
              {ageLimitDays !== '' && <small className="info-text">Brukere kan kun se rapporter fra de siste {ageLimitDays} dagene</small>}
              {ageLimitError && <div className="error-message">{ageLimitError}</div>}
              {ageLimitSaved && <div className="success-message">Innstillinger lagret!</div>}
            </div>
          </div>
        </div>
      )}

      <h2 className="section-heading">Brukere</h2>

      <div className="users-table-container">
        <table className="users-table">
          <thead>
            <tr>
              <th>Brukernavn</th>
              <th>Fullt navn</th>
              <th>Administrator</th>
              <th>Status</th>
              <th>E-post</th>
              <th>Opprettet</th>
              <th>Handlinger</th>
            </tr>
          </thead>
          <tbody>
            {users.map(user => {
              // Handle is_active as both integer and string from SQLite
              const isActive = user.is_active === 1 || user.is_active === '1';
              const emailDigestAllowed = user.email_digest_allowed === 1 || user.email_digest_allowed === '1';

              return (
              <tr key={user.id} className={!isActive ? 'user-disabled' : ''}>
                <td data-label="Brukernavn">{user.username}</td>
                <td data-label="Fullt navn">{user.full_name}</td>
                <td data-label="Administrator">{user.is_admin ? 'Ja' : 'Nei'}</td>
                <td data-label="Status">
                  {isActive ? (
                    <span className="status-badge status-active">Aktiv</span>
                  ) : (
                    <span className="status-badge status-disabled">Deaktivert</span>
                  )}
                </td>
                <td data-label="E-post">
                  {emailDigestAllowed ? (
                    <span className="status-badge status-active">Aktivert</span>
                  ) : (
                    <span className="status-badge status-disabled">Deaktivert</span>
                  )}
                </td>
                <td data-label="Opprettet">{formatDate(user.created_at)}</td>
                <td data-label="Handlinger">
                  <button
                    onClick={() => handleEdit(user)}
                    className="btn-small"
                  >
                    Rediger
                  </button>
                  {isActive ? (
                    <button
                      onClick={() => handleDisable(user.id)}
                      className="btn-small btn-danger"
                    >
                      Deaktiver
                    </button>
                  ) : (
                    <button
                      onClick={() => handleEnable(user.id)}
                      className="btn-small btn-success"
                    >
                      Aktiver
                    </button>
                  )}
                  {emailDigestAllowed ? (
                    <button
                      onClick={() => handleDisableEmailDigest(user.id)}
                      className="btn-small btn-danger"
                    >
                      Deaktiver e-post
                    </button>
                  ) : (
                    <button
                      onClick={() => handleEnableEmailDigest(user.id)}
                      className="btn-small btn-success"
                    >
                      Aktiver e-post
                    </button>
                  )}
                </td>
              </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {showModal && (
        <div className="modal-overlay" onClick={handleCloseModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>{editingUser ? 'Rediger bruker' : 'Opprett ny bruker'}</h2>

            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label htmlFor="username">Brukernavn *</label>
                <input
                  type="text"
                  id="username"
                  value={formData.username}
                  onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="password">
                  Passord {editingUser ? '(la stå tomt for å beholde nåværende)' : '*'}
                </label>
                <input
                  type="password"
                  id="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  required={!editingUser}
                  placeholder={editingUser ? 'Skriv nytt passord for å endre' : ''}
                />
              </div>

              <div className="form-group">
                <label htmlFor="full_name">Fullt navn *</label>
                <input
                  type="text"
                  id="full_name"
                  value={formData.full_name}
                  onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                  required
                />
              </div>

              <div className="form-group checkbox-group">
                <label>
                  <input
                    type="checkbox"
                    checked={formData.is_admin}
                    onChange={(e) => setFormData({ ...formData, is_admin: e.target.checked })}
                  />
                  <span>Administrator</span>
                </label>
              </div>

              <div className="modal-actions">
                <button
                  type="button"
                  onClick={handleCloseModal}
                  className="btn-secondary"
                >
                  Avbryt
                </button>
                <button type="submit" className="btn-primary">
                  {editingUser ? 'Oppdater bruker' : 'Opprett bruker'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default Admin;
